import { useContext, useEffect, useState, memo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { Row as TRow } from "react-table";
import { useTranslation } from 'react-i18next';
import useAlert from '../../../../Alerts/useAlert';
import usePrivileges from '../../../../Auth/usePrivileges';
import { LotDetailContext } from '../../../../Contexts/LotDetailContext';
import SelectFilter from '../../../../DataTable/datatable.selectfilter';
import QirColumn from '../../../../DataTable/datatable.qircolumn';
import EditableDatatable, { EditableDatatableAPI } from '../../../../DataTable/Editable/datatable.editable';
import EditableDateColumn from '../../../../DataTable/Editable/datatable.editable.datecolumn';
import EditableColumn from '../../../../DataTable/Editable/datatable.editable.inputcolumn';
import EditableSelectColumn from '../../../../DataTable/Editable/datatable.editable.selectcolumn';
import ConfirmationDialog from '../../../../Dialogs/ConfirmationDialog';
import { Changes } from '../../../../Types/Changes';
import { Package } from '../../../../Types/Package';
import { PackageType } from '../../../../Types/PackageType';
import WithComment from '../../../../Types/WithComment';
import { usePackageTypes } from '../../../../Utilities/Hooks';
import { useHttpUtils } from '../../../../Utilities/useHttpUtils';
import PackageNumberColumn from './PackageNumberColumn';
import AddPackagesDialog from './AddPackagesDialog';

import { DateTime } from 'luxon';
import DateTimePicker from '../../../../Components/DateTimePickerV2';
import { convertToDateTime } from "../../../../Utilities/useDateUtils";

import './Packages.css';

export type PackageColumnType = "PackageNumber"
    | "PackageDate"
    | "PackagedAtDate"
    | "ManufactureDate"
    | "Grade"
    | "Status"
    | "Type"
    | "TareWeight"
    | "NetWeight"
    | "SiteCode"
    | "Scale"
    | "RedDot"
    | "Notes"
    | "QIRs"
    | "Rfid";

interface PackagesSectionProps {
    packagesCanBeEdited: boolean,
    customColumns?: PackageColumnType[]
}

interface SetRedDotCodesDialogProps {
    api: EditableDatatableAPI<Package>,
    showDialog: boolean,
    closeDialog: Function,
    fromUtc: DateTime | null,
    toUtc: DateTime | null
};

const SetRedDotCodesDialog = function ({ api, showDialog, closeDialog, fromUtc, toUtc }: SetRedDotCodesDialogProps) {
    const { t } = useTranslation();

    const [rangeFromUtc, setRangeFromUtc] = useState(fromUtc);
    const [rangeToUtc, setRangeToUtc] = useState(toUtc);
    const [redDotCode, setRedDotCode] = useState("");
    const [notes, setNotes] = useState("");

    const OnOk = function () {
        var rowsInRange = api.filteredRows.filter((r: TRow<Package>) =>
            (rangeFromUtc == null || convertToDateTime(r.original.packageDateTimeUtc) >= rangeFromUtc)
            && (rangeToUtc == null || convertToDateTime(r.original.packageDateTimeUtc) <= rangeToUtc)
            && (r.original.redDotCode === null || r.original.redDotCode === "")
        );

        for (let i = 0; i < rowsInRange.length; i++) {
            api.updateData("redDotCode", rowsInRange[i], redDotCode);
            api.updateData("notes", rowsInRange[i], notes);
        }
    }

    return (
        <ConfirmationDialog
            title={t<string>("LotDetails.Packages.SetRedDotCodes")}
            showDialog={showDialog}
            onOk={() => { OnOk() }}
            closeDialog={() => closeDialog()}>
            <>
                <Row>
                    <Col xs={12} xl>
                        <Form.Group as={Row} controlId="dateFrom">
                            <Form.Label column xs={2} sm={4}>{t("Filter.DateFrom")}:</Form.Label>
                            <Col xs={10} sm={8}>
                                <DateTimePicker name="dateFrom" dateTimeUTC={rangeFromUtc} handleDateSelect={(date) => setRangeFromUtc(date)} />
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} xl>
                        <Form.Group as={Row} controlId="dateTo">
                            <Form.Label column xs={2} sm={4}>{t("Filter.DateTo")}:</Form.Label>
                            <Col xs={10} sm={8}>
                                <DateTimePicker name="dateTo" dateTimeUTC={rangeToUtc} handleDateSelect={(date) => setRangeToUtc(date)} />
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} xl>
                        <Form.Group as={Row} controlId="redDotCode">
                            <Form.Label column xs={2} sm={4}>Red Dot Code:</Form.Label>
                            <Col xs={4} sm={4}>
                                <Form.Control type="text" placeholder="Code" value={redDotCode} name="reddotcode" onChange={(e) => { setRedDotCode(e.currentTarget.value); }} />
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} xl>
                        <Form.Group as={Row} controlId="notes">
                            <Form.Label column xs={2} sm={4}>Notes:</Form.Label>
                            <Col xs={4} sm={4}>
                                <Form.Control type="text" placeholder="Notes" value={notes} name="notes" onChange={(e) => { setNotes(e.currentTarget.value); }} />
                            </Col>
                        </Form.Group>
                    </Col>
                </Row>
            </>
        </ConfirmationDialog>
    );
}

const PackagesSection = memo(function PackagesSection({ packagesCanBeEdited, customColumns = undefined }: PackagesSectionProps) {
    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const {
        lotDetail: {
            summary: {
                lotNumber,
                businessUnitCode,
                grade,
                buildStartUtc,
                buildEndUtc
            },
            packages,
            qirs
        },
        getLotDetail,
        lotFlags: {
            hasRedDotCodes
        }
    } = useContext(LotDetailContext);

    const { canReview } = usePrivileges(businessUnitCode);
    const packageTypes: PackageType[] = usePackageTypes(null, businessUnitCode, grade, true);
    const [canEditPrivilege, setCanEditPrivilege] = useState(!!canReview && packagesCanBeEdited);

    useEffect(() => {
        setCanEditPrivilege(!!canReview && packagesCanBeEdited);
    }, [canReview, packagesCanBeEdited]);

    const packageTypeOptions = packageTypes.map(x => ({
        value: x.name,
        display: x.alternativeName ? `${x.name} (${x.alternativeName})` : x.name
    }));

    const getCellProps = (cell: any) => {
        return { className: "align-middle" };
    };

    const defaultColumns: PackageColumnType[] = [
        "PackageNumber",
        "PackageDate",
        "Grade",
        "Status",
        "Type",
        "TareWeight",
        "NetWeight",
        "SiteCode",
        "Scale",
        "RedDot",
        "Notes",
        "QIRs"
    ];

    const packageColumnTypeToColumn = (columnType: PackageColumnType): any => {
        switch (columnType) {
            case "PackageNumber":
                return PackageNumberColumn({ width: "10%" });
            case "PackageDate":
                return EditableDateColumn('LotDetails.Packages.PackageDateTime', 'packageDateTimeUtc', canEditPrivilege, { width: "10%" });
            case "PackagedAtDate":
                return EditableDateColumn('LotDetails.Packages.PackagedAtDateTime', 'packagedAtDateTimeUtc', canEditPrivilege, { width: "10%" });
            case "ManufactureDate":
                return EditableDateColumn('LotDetails.Packages.ManufactureDateTime', 'manufactureDateTimeUtc', canEditPrivilege, { width: "10%" });
            case "Grade":
                return EditableColumn('LotDetails.Packages.Grade', 'grade', canEditPrivilege, 'equals', SelectFilter);
            case "Status":
                return EditableColumn('LotDetails.Packages.Status', 'status', canEditPrivilege, 'equals', SelectFilter);
            case "Type":
                return EditableSelectColumn('LotDetails.Packages.PackageType', 'packageType', packageTypeOptions, canEditPrivilege, 'equals', SelectFilter, { width: "15%" });
            case "TareWeight":
                return EditableColumn('LotDetails.Packages.TareWeight', 'tareWeight', canEditPrivilege, 'includesString');
            case "NetWeight":
                return EditableColumn('LotDetails.Packages.NetWeight', 'netWeight', canEditPrivilege, 'includesString');
            case "SiteCode":
                return EditableColumn('LotDetails.Packages.SiteCode', 'siteCode', canEditPrivilege, 'equals', SelectFilter);
            case "Scale":
                return EditableColumn('LotDetails.Packages.Scale', 'scale', canEditPrivilege, 'equals', SelectFilter);
            case "RedDot":
                return EditableColumn('LotDetails.Packages.RedDot', 'redDotCode', canEditPrivilege, 'equals', SelectFilter);
            case "Notes":
                return EditableColumn('LotDetails.Packages.Notes', 'notes', canEditPrivilege, 'equals', SelectFilter, { width: "10%" });
            case "QIRs":
                return QirColumn({ header: t<string>('LotDetails.Packages.QIRs'), accessor: 'qirIncidentIds', qirs });
            case "Rfid":
                return EditableColumn('LotDetails.Packages.RFID', 'rfid', canEditPrivilege, 'equals', SelectFilter, { width: "10%" });
        }
    }

    const defaultTableColumns = [
        PackageNumberColumn({ width: "10%" }),
        EditableDateColumn('LotDetails.Packages.PackageDateTime', 'packageDateTimeUtc', canEditPrivilege, { width: "10%" }),
        EditableColumn('LotDetails.Packages.Grade', 'grade', canEditPrivilege, 'equals', SelectFilter),
        EditableColumn('LotDetails.Packages.Status', 'status', canEditPrivilege, 'equals', SelectFilter),
        EditableSelectColumn('LotDetails.Packages.PackageType', 'packageType', packageTypeOptions, canEditPrivilege, 'equals', SelectFilter, { width: "15%" }),
        EditableColumn('LotDetails.Packages.TareWeight', 'tareWeight', canEditPrivilege, 'includesString'),
        EditableColumn('LotDetails.Packages.NetWeight', 'netWeight', canEditPrivilege, 'includesString'),
        EditableColumn('LotDetails.Packages.SiteCode', 'siteCode', canEditPrivilege, 'equals', SelectFilter),
        EditableColumn('LotDetails.Packages.Scale', 'scale', canEditPrivilege, 'equals', SelectFilter),
        EditableColumn('LotDetails.Packages.RedDot', 'redDotCode', canEditPrivilege, 'equals', SelectFilter),
        EditableColumn('LotDetails.Packages.Notes', 'notes', canEditPrivilege, 'equals', SelectFilter, { width: "10%" }),
        QirColumn({ header: t<string>('LotDetails.Packages.QIRs'), accessor: 'qirIncidentIds', qirs })
    ];

    // The set of columns to actually display (may be customized)
    let packageColumns = (customColumns && customColumns.length > 0) ? customColumns : defaultColumns;

    var tableColumns = packageColumns.map(col => packageColumnTypeToColumn(col));

    const initialState = {
        sortBy: [
            {
                id: 'LotDetails.Packages.PackageDateTime',
                desc: false
            },
            {
                id: 'LotDetails.Packages.BoxNumber',
                desc: false
            }
        ],
        hiddenColumns: packages.every(x => x.notes === lotNumber) ? ["LotDetails.Packages.Notes"] : []
    };

    const [loading, setLoading] = useState(false);
    const saveChanges = async (data: Changes<Package>, comment: string) => {
        let url = `/api/v1/lot/${lotNumber}/packages`;
        let body: WithComment<Changes<Package>> = {
            data: data,
            comment: comment,
        };

        setLoading(true);
        await fetchDataAuthenticated(url, 'PUT', JSON.stringify(body))
            .then(async (response: any) => {
                if (response.ok) {
                    setAlert("success", "Success", `Updated the packages`);
                    getLotDetail();
                } else {
                    let errorMessage = await response.text();
                    console.log("Failed to update the package:" + errorMessage);
                    throw new Error("Failed to update the package.");
                }
            })
            .catch((error: any) => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => setLoading(false));
    };

    const [showClearConfirmationDialog, setShowClearConfirmationDialog] = useState(false);
    const [showSetRedDotCodesDialog, setShowSetRedDotCodesDialog] = useState(false);
    const [showAddPackagesDialog, setShowAddPackagesDialog] = useState(false);

    if (packages?.length === 0) {
        return (
            <>{t('CommonResponses.NoData')}</>
        );
    }

    const getRowProps = (row: TRow<Package>) => {
        let className = "";

        if (row.original.redDotCode !== "") {
            className += " package-hasreddot"
        }

        if (row.original.qirIncidentIds.length > 0) {
            className += " package-hasqir"
        }

        return { className: className };
    }

    return (
        <EditableDatatable identifier={'containerNumber'} tableColumns={tableColumns ?? defaultTableColumns} initialTableData={packages} getRowProps={getRowProps} getCellProps={getCellProps} initialState={initialState} saveChanges={saveChanges} canDelete={canEditPrivilege}>
            {(api) => (canEditPrivilege &&
                <>
                    {hasRedDotCodes &&
                        <>
                            <Button onClick={() => setShowClearConfirmationDialog(true)}>
                                {t<string>("LotDetails.Packages.ClearRedDotCodes")}
                            </Button>
                            <Button onClick={() => setShowSetRedDotCodesDialog(true)} className="ms-2">
                                {t<string>("LotDetails.Packages.SetRedDotCodes")}
                            </Button>
                        </>
                    }
                    <Button onClick={() => setShowAddPackagesDialog(true)} className="ms-2">
                        {t<string>("Application.Controls.Add")}
                    </Button>
                    <AddPackagesDialog
                        showDialog={showAddPackagesDialog}
                        closeDialog={() => setShowAddPackagesDialog(false)}
                        packageTypeOptions={packageTypeOptions}
                        addPackage={api.createData}
                    />
                    <ConfirmationDialog
                        title={t<string>("LotDetails.Packages.ClearRedDotCodes")}
                        showDialog={showClearConfirmationDialog}
                        onOk={() => api.applyToFilteredRows('redDotCode', '', true)}
                        closeDialog={() => setShowClearConfirmationDialog(false)}>
                        <p>{t<string>("LotDetails.Packages.ClearRedDotCodesWarning", { redDotCodes: [...new Set(api.filteredRows.map(r => r.original.redDotCode))].filter(code => code) })}</p>
                </ConfirmationDialog>
                <SetRedDotCodesDialog fromUtc={buildStartUtc} api={api} toUtc={buildEndUtc} showDialog={showSetRedDotCodesDialog} closeDialog={() => { setShowSetRedDotCodesDialog(false); }}></SetRedDotCodesDialog>
                </>
            )}
        </EditableDatatable>
    );
});

export default PackagesSection;